import { Link } from "react-router-dom";
import { ArrowRight, Smartphone, Monitor, Globe, Radio } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import SystemDiagram from "@/components/SystemDiagram";
import CTASection from "@/components/CTASection";
import MeshCanvas from "@/components/MeshCanvas";
import { Reveal, Overline } from "@/components/Reveal";
import { MeshRings } from "@/components/Vectors";
import { COMMISSIONING, CAPABILITIES } from "@/data/content";
import { CAPABILITY_ICONS, STEP_ICONS } from "@/lib/icons";
import { track } from "@/lib/api";

export default function Technology() {
  return (
    <div className="bg-white" data-testid="technology-page">
      <SEO
        title="Technology — Bluetooth Mesh Lighting Control"
        description="How Amaya Pro works: Bluetooth Mesh networking, sensor-to-software architecture, commissioning workflow and the Amaya Pro web & app system."
        path="/technology"
      />
      <PageHero
        dark
        overline="Technology"
        title="Engineered as a system, not a sensor."
        sub="Amaya Pro is a complete Network Lighting Control architecture — from the sensor on the fixture to the energy data in the portal."
        artwork={<MeshRings className="w-full h-auto" />}
      />

      {/* BLUETOOTH MESH */}
      <section className="relative border-t border-zinc-200 py-24 md:py-32 overflow-hidden" data-testid="mesh-explainer">
        <MeshCanvas className="absolute inset-0 w-full h-full opacity-50" density={50} />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-transparent to-white pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Overline>Bluetooth Mesh</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
              Many-to-many, by design.
            </h2>
            <p className="text-zinc-500 text-base md:text-lg mt-6 leading-relaxed">
              Bluetooth Mesh is a secure, low-power wireless protocol that enables many-to-many communication between lighting devices. Every node relays for the network — providing scalable, reliable and resilient control across large facilities, with no single point of failure.
            </p>
            <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mt-10 text-center">
              {[["Up to", "328 ft", "extended range"], ["Security", "PSA L3", "device-level"], ["Topology", "Mesh", "many-to-many"]].map(([k, v, s]) => (
                <div key={v} className="bg-white p-5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{k}</p>
                  <p className="font-display text-xl md:text-2xl font-bold mt-1 text-signal">{v}</p>
                  <p className="text-[11px] text-zinc-500 mt-1">{s}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-zinc-200 bg-white/90 backdrop-blur p-8">
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-6">Device → Device → Device</p>
              <div className="grid grid-cols-4 gap-3">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="aspect-square border border-zinc-200 bg-bone flex items-center justify-center">
                    <span
                      className={`rounded-full ${i % 5 === 0 ? "w-2.5 h-2.5 bg-signal" : "w-1.5 h-1.5 bg-zinc-400"} animate-pulse-dot`}
                      style={{ animationDelay: `${i * 0.22}s` }}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
                Each Amaya Pro device joins the mesh and strengthens it. Long Range Extenders (06970 / 06971) push Bluetooth range up to 328 ft and 200 ft respectively.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ARCHITECTURE */}
      <section className="border-t border-zinc-200 py-24 md:py-32" data-testid="architecture">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>System architecture</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
              Five layers. One system.
            </h2>
          </Reveal>
          <div className="mt-14">
            <SystemDiagram dark />
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="border-t border-zinc-200 py-24 md:py-32" data-testid="tech-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>System capabilities</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
              Everything a networked system should do.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mt-14">
            {CAPABILITIES.map((c, i) => {
              const CapIcon = CAPABILITY_ICONS[c.name];
              return (
              <Reveal key={c.name} delay={(i % 3) * 0.05} className="bg-white">
                <div className="p-6 h-full hover:bg-bone transition-colors">
                  {CapIcon && <CapIcon size={20} className="text-signal mb-3" strokeWidth={1.6} />}
                  <h3 className="font-display text-lg font-bold">{c.name}</h3>
                  <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOFTWARE */}
      <section className="bg-bone text-ink py-24 md:py-32" data-testid="software">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline dark={false}>Amaya Pro System — Web & App</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
              Commission, control and monitor — from anywhere.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {[
              { icon: Smartphone, t: "Mobile App", d: "Broadcast-control all luminaires on the 0–10V line, set personalized preferences and manage locations from the app." },
              { icon: Monitor, t: "Desktop Friendly", d: "Full desktop access for programming, floor-wide management and energy data review." },
              { icon: Globe, t: "Online Portal", d: "A robust portal for pre-planning, pre & post programming, location controls and energy data across sites." },
            ].map((s, i) => (
              <Reveal key={s.t} delay={i * 0.1}>
                <div className="bg-white border border-zinc-200 p-8 h-full hover:border-signal transition-colors" data-testid={`software-${s.t.toLowerCase().replace(/ /g, "-")}`}>
                  <s.icon size={26} className="text-signal" />
                  <h3 className="font-display text-xl font-bold mt-5">{s.t}</h3>
                  <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="text-sm text-zinc-500 mt-8 max-w-2xl leading-relaxed border-l-2 border-signal pl-4">
              Controls are programmed via the commissioning remote, and a handheld remote is available for on-demand custom user preference. Interface imagery shown across this site is conceptual; actual app screens are shared during a live demo.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMMISSIONING */}
      <section className="border-t border-zinc-200 py-24 md:py-32" data-testid="commissioning">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Commissioning</Overline>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl">
              Plan to optimize in six steps.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mt-14">
            {COMMISSIONING.map((s, i) => {
              const StepIcon = STEP_ICONS[i];
              return (
              <Reveal key={s.n} delay={(i % 3) * 0.08}>
                <div className="border-t border-zinc-200 pt-6 group" data-testid={`step-${s.n}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-3xl text-signal/80 group-hover:text-signal transition-colors">{s.n}</span>
                    {StepIcon && <StepIcon size={20} className="text-zinc-400 group-hover:text-signal transition-colors" strokeWidth={1.6} />}
                  </div>
                  <h3 className="font-display text-2xl font-bold mt-3 uppercase tracking-tight">{s.t}</h3>
                  <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact?type=demo"
              onClick={() => track("cta_click", { cta: "see_how_it_works", location: "technology" })}
              className="inline-flex items-center gap-2 mt-14 px-7 py-3.5 bg-signal text-white font-medium hover:bg-[#5c8f23] transition-colors"
              data-testid="tech-demo-btn"
            >
              See How Amaya Works <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection title="See the system running live." sub="We'll walk your team through a live Amaya Pro commissioning session." />
    </div>
  );
}
